import { useQuery } from '@tanstack/react-query';
import { http, returnIfNotFoundError } from 'services/http';
import { CONTACTS_GET_MANY } from 'api/query-keys.ts';
import type { ContactRes } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query';

interface IParams extends IWithPagination {
  search?: string;
  clientId?: number;
}

export const useGetContacts = (params?: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, search, clientId } = params || {};

  const qs = new URLSearchParams({ limit: String(limit), page: String(page), 'filter[clientId]': String(clientId) });
  if (search) qs.set('filter[search]', search);

  return useQuery<WithPagination<ContactRes[]>>({
    queryKey: search ? [CONTACTS_GET_MANY, clientId, search] : [CONTACTS_GET_MANY, clientId],
    queryFn: async () => {
      try {
        const { data, headers } = await http.get<ContactRes[]>(`/v1/contacts?${qs.toString()}`);
        const pagination = preparePagination(headers);

        return { data, pagination };
      } catch (error) {
        return returnIfNotFoundError<WithPagination<[]>>(error, { data: [], pagination: null });
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    cacheTime: 30,
    enabled: !!params?.clientId,
  });
};
